import { Col, Form } from "antd";
import type { FormInstance } from "antd/es/form/Form";
import { blankToUndefined, optionalNonNegativeValidator } from "components/Form/components/Validations/validationRules/NonNegative";
import i18n from "i18next";
import { type FC, useMemo } from "react";

import type { ProductBatchUpdateEntity } from "src/app/features/Product/types/ProductBatchUpdateEntity";
import { DrawerForm } from "../../../../../components/DrawerForm/DrawerForm";
import { BatchUpdateFormRow } from "../../../../../components/DrawerForm/drawerFormStyles";
import { FieldLabel } from "../../../../../components/DrawerForm/FieldLabel";
import { Checkbox, SelectDynamic, TextInput } from "../../../../../components/Form/formComponents";
import { NumberInput } from "../../../../../components/Form/formComponents/Number/NumberInput";
import { useLoadFormDefinitionQuery } from "../../../../../components/Form/services/server/serverApi";
import type { SearchFormProps } from "../../../../../components/Form/types/types";
import {
    productDefaultUnitSelectorConfig,
    productSpecialTypeSelectorConfig,
    productTypeSelectorConfig,
    vatCodeSelectConfig,
} from "../../../../../components/SearchForm/FormSearchUtils";

type ProductBatchUpdateFormProps = SearchFormProps<ProductBatchUpdateEntity> & { form?: FormInstance };

export const ProductBatchUpdateForm: FC<ProductBatchUpdateFormProps> = ({ searchCallback, form }) => {
    const { data: formDefinition } = useLoadFormDefinitionQuery("product");

    // Only expose fields that exist in the current Data Group's Product form config.
    const availableFieldNames = useMemo(
        () => new Set(formDefinition?.formConfiguration?.tabs.flatMap((tab) => tab.fields.map((field) => field.name)) ?? []),
        [formDefinition],
    );

    const isFieldAvailable = (fieldName: keyof ProductBatchUpdateEntity) => availableFieldNames.has(fieldName);

    return (
        <DrawerForm onSubmit={searchCallback} form={form} useSubmitButton={true} submitLabel={i18n.t("button.batchUpdate")}>
            <p>{i18n.t("batchUpdate.message")}</p>

            {isFieldAvailable("grCode") && (
                <>
                    <FieldLabel label={i18n.t("productCreateForm.grCode")} isRequiredField />
                    <BatchUpdateFormRow>
                        <Col span={16}>
                            <Form.Item
                                name="grCode"
                                dependencies={["updateGrCode"]}
                                rules={[
                                    ({ getFieldValue }) => ({
                                        required: getFieldValue("updateGrCode"),
                                        message: i18n.t("common.input.required.error"),
                                    }),
                                ]}
                            >
                                <TextInput name="grCode" label={""} readOnly={false} />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="updateGrCode" valuePropName="checked">
                                <Checkbox name="updateGrCode" label="batchUpdate.updateField" readOnly={false} defaultChecked={false} />
                            </Form.Item>
                        </Col>
                    </BatchUpdateFormRow>
                </>
            )}

            {isFieldAvailable("status") && (
                <>
                    <FieldLabel label={i18n.t("form.status")} />
                    <BatchUpdateFormRow>
                        <Col span={16}>
                            <Form.Item name="status" valuePropName="checked">
                                <Checkbox name="batchStatus" label="" readOnly={true} defaultChecked={true} />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="updateStatus" valuePropName="checked">
                                <Checkbox name="updateStatus" label="batchUpdate.updateField" readOnly={false} defaultChecked={false} />
                            </Form.Item>
                        </Col>
                    </BatchUpdateFormRow>
                </>
            )}

            {isFieldAvailable("name") && (
                <>
                    <FieldLabel label={i18n.t("form.name")} isRequiredField />
                    <BatchUpdateFormRow>
                        <Col span={16}>
                            <Form.Item
                                name="name"
                                dependencies={["updateName"]}
                                rules={[
                                    ({ getFieldValue }) => ({
                                        required: getFieldValue("updateName"),
                                        message: i18n.t("common.input.required.error"),
                                    }),
                                ]}
                            >
                                <TextInput name="name" label={""} readOnly={false} />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="updateName" valuePropName="checked">
                                <Checkbox name="updateName" label="batchUpdate.updateField" readOnly={false} defaultChecked={false} />
                            </Form.Item>
                        </Col>
                    </BatchUpdateFormRow>
                </>
            )}

            {isFieldAvailable("shortName") && (
                <>
                    <FieldLabel label={i18n.t("productCreateForm.shortName")} isRequiredField />
                    <BatchUpdateFormRow>
                        <Col span={16}>
                            <Form.Item
                                name="shortName"
                                dependencies={["updateShortName"]}
                                rules={[
                                    ({ getFieldValue }) => ({
                                        required: getFieldValue("updateShortName"),
                                        message: i18n.t("common.input.required.error"),
                                    }),
                                ]}
                            >
                                <TextInput name="shortName" label={""} readOnly={false} />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="updateShortName" valuePropName="checked">
                                <Checkbox name="updateShortName" label="batchUpdate.updateField" readOnly={false} defaultChecked={false} />
                            </Form.Item>
                        </Col>
                    </BatchUpdateFormRow>
                </>
            )}

            {isFieldAvailable("type") && (
                <>
                    <FieldLabel label={i18n.t("form.type")} isRequiredField />
                    <BatchUpdateFormRow>
                        <Col span={16}>
                            <Form.Item
                                name="type"
                                dependencies={["updateType"]}
                                rules={[
                                    ({ getFieldValue }) => ({
                                        required: getFieldValue("updateType"),
                                        message: i18n.t("common.input.required.error"),
                                    }),
                                ]}
                            >
                                <SelectDynamic name="type" label={""} fieldConfig={productTypeSelectorConfig} />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="updateType" valuePropName="checked">
                                <Checkbox name="updateType" label="batchUpdate.updateField" readOnly={false} defaultChecked={false} />
                            </Form.Item>
                        </Col>
                    </BatchUpdateFormRow>
                </>
            )}

            {isFieldAvailable("defaultUnit") && (
                <>
                    <FieldLabel label={i18n.t("productCreateForm.defaultUnit")} isRequiredField />
                    <BatchUpdateFormRow>
                        <Col span={16}>
                            <Form.Item
                                name="defaultUnit"
                                dependencies={["updateDefaultUnit"]}
                                rules={[
                                    ({ getFieldValue }) => ({
                                        required: getFieldValue("updateDefaultUnit"),
                                        message: i18n.t("common.input.required.error"),
                                    }),
                                ]}
                            >
                                <SelectDynamic name="defaultUnit" label={""} fieldConfig={productDefaultUnitSelectorConfig} />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="updateDefaultUnit" valuePropName="checked">
                                <Checkbox
                                    name="updateDefaultUnit"
                                    label="batchUpdate.updateField"
                                    readOnly={false}
                                    defaultChecked={false}
                                />
                            </Form.Item>
                        </Col>
                    </BatchUpdateFormRow>
                </>
            )}

            {isFieldAvailable("productCode") && (
                <>
                    <FieldLabel label={i18n.t("productCreateForm.productCode")} isRequiredField />
                    <BatchUpdateFormRow>
                        <Col span={16}>
                            <Form.Item
                                name="productCode"
                                dependencies={["updateProductCode"]}
                                rules={[
                                    ({ getFieldValue }) => ({
                                        required: getFieldValue("updateProductCode"),
                                        message: i18n.t("common.input.required.error"),
                                    }),
                                ]}
                            >
                                <TextInput name="productCode" label={""} readOnly={false} />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="updateProductCode" valuePropName="checked">
                                <Checkbox
                                    name="updateProductCode"
                                    label="batchUpdate.updateField"
                                    readOnly={false}
                                    defaultChecked={false}
                                />
                            </Form.Item>
                        </Col>
                    </BatchUpdateFormRow>
                </>
            )}

            {isFieldAvailable("vatCode") && (
                <>
                    <FieldLabel label={i18n.t("productCreateForm.vatCode")} isRequiredField />
                    <BatchUpdateFormRow>
                        <Col span={16}>
                            <Form.Item
                                name="vatCode"
                                dependencies={["updateVatCode"]}
                                rules={[
                                    ({ getFieldValue }) => ({
                                        required: getFieldValue("updateVatCode"),
                                        message: i18n.t("common.input.required.error"),
                                    }),
                                ]}
                            >
                                <SelectDynamic name="vatCode" label={""} fieldConfig={vatCodeSelectConfig} />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="updateVatCode" valuePropName="checked">
                                <Checkbox name="updateVatCode" label="batchUpdate.updateField" readOnly={false} defaultChecked={false} />
                            </Form.Item>
                        </Col>
                    </BatchUpdateFormRow>
                </>
            )}

            {isFieldAvailable("price") && (
                <>
                    <FieldLabel label={i18n.t("productCreateForm.price")} />
                    <BatchUpdateFormRow>
                        <Col span={16}>
                            <Form.Item
                                name="price"
                                rules={[{ validator: optionalNonNegativeValidator }]}
                                getValueFromEvent={blankToUndefined}
                            >
                                <NumberInput name="price" readOnly={false} />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="updatePrice" valuePropName="checked">
                                <Checkbox name="updatePrice" label="batchUpdate.updateField" readOnly={false} defaultChecked={false} />
                            </Form.Item>
                        </Col>
                    </BatchUpdateFormRow>
                </>
            )}

            {isFieldAvailable("numberOfUnits") && (
                <>
                    <FieldLabel label={i18n.t("productCreateForm.numberOfUnits")} />
                    <BatchUpdateFormRow>
                        <Col span={16}>
                            <Form.Item
                                name="numberOfUnits"
                                rules={[{ validator: optionalNonNegativeValidator }]}
                                getValueFromEvent={blankToUndefined}
                            >
                                <NumberInput name="numberOfUnits" readOnly={false} />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="updateNumberOfUnits" valuePropName="checked">
                                <Checkbox
                                    name="updateNumberOfUnits"
                                    label="batchUpdate.updateField"
                                    readOnly={false}
                                    defaultChecked={false}
                                />
                            </Form.Item>
                        </Col>
                    </BatchUpdateFormRow>
                </>
            )}

            {isFieldAvailable("specialType") && (
                <>
                    <FieldLabel label={i18n.t("productCreateForm.specialType")} />
                    <BatchUpdateFormRow>
                        <Col span={16}>
                            <Form.Item name="specialType">
                                <SelectDynamic name="specialType" label={""} fieldConfig={productSpecialTypeSelectorConfig} />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="updateSpecialType" valuePropName="checked">
                                <Checkbox
                                    name="updateSpecialType"
                                    label="batchUpdate.updateField"
                                    readOnly={false}
                                    defaultChecked={false}
                                />
                            </Form.Item>
                        </Col>
                    </BatchUpdateFormRow>
                </>
            )}

            {isFieldAvailable("loyaltyProductCode") && (
                <>
                    <FieldLabel label={i18n.t("productCreateForm.loyaltyProductCode")} />
                    <BatchUpdateFormRow>
                        <Col span={16}>
                            <Form.Item name="loyaltyProductCode">
                                <TextInput name="loyaltyProductCode" label={""} readOnly={false} />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="updateLoyaltyProductCode" valuePropName="checked">
                                <Checkbox
                                    name="updateLoyaltyProductCode"
                                    label="batchUpdate.updateField"
                                    readOnly={false}
                                    defaultChecked={false}
                                />
                            </Form.Item>
                        </Col>
                    </BatchUpdateFormRow>
                </>
            )}
        </DrawerForm>
    );
};
